import React, { useState } from 'react';
import clsx from 'clsx';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { motion } from 'motion/react';

import {
  Bars3Icon,
  XMarkIcon,
  InboxArrowDownIcon,
  DocumentCheckIcon,
  PencilSquareIcon,
  ChartBarIcon,
  BriefcaseIcon,
  InboxStackIcon,
  ChatBubbleBottomCenterTextIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { OysterLogo } from '@oysterjs/uiv2/logo';
import { PrimaryButton } from '@oysterjs/uiv2/button';
import { getAisBaseUrl } from '@oysterjs/corev2/config';

const demoLink = 'https://share.hsforms.com/1_2mhmmLZREOe4wb1Mf0rWQdbrer';

const navigation = [
  { name: 'Product', href: '#product' },
  { name: 'Use Cases', href: '#features' },
  { name: 'Integrations', href: '#integrations' },
  { name: 'Book a Demo', href: demoLink, target: '_blank' }
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const loginLink = getAisBaseUrl() + '/app';

  return (
    <header className="px-8 lg:px-12 absolute inset-x-0 top-0 z-50">
      <nav aria-label="Global" className="mx-auto max-w-7xl flex items-center justify-between py-8">
        <div className="flex lg:flex-1">
          <a href="#" className="-m-1.5 p-1.5">
            <span className="sr-only">Oyster Technologies, Inc.</span>
            <OysterLogo scale={0.7} />
          </a>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-neutral-600"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
        <div className="hidden lg:flex lg:gap-x-8">
          {navigation.map((item) => (
            <a
              key={item.name}
              href={item.href}
              target={item.target}
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm/6 font-semibold text-neutral-900 rounded-full px-4 py-1 hover:bg-primary-500 hover:text-white transition-colors duration-100"
            >
              {item.name}
            </a>
          ))}
        </div>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <a
            href={loginLink}
            className={clsx(
              'text-sm/6 font-semibold text-neutral-900 rounded-full px-4 py-1 hover:bg-primary-500 hover:text-white transition-colors duration-100'
            )}
          >
            Log in <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-neutral-950/50 px-2 py-2 transition duration-200 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16 dark:bg-neutral-950/75"
        />
        <DialogPanel
          transition
          className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-8 py-8 sm:max-w-sm sm:ring-1 sm:ring-neutral-900/10 transform transition-all duration-200 data-[closed]:translate-x-1/2 data-[closed]:opacity-0"
        >
          <div className="flex items-center justify-between">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">Oyster Technologies, Inc.</span>
              <OysterLogo scale={0.7} />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-neutral-600"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-neutral-500/10">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target={item.target}
                    onClick={() => setMobileMenuOpen(false)}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-neutral-900 hover:bg-neutral-50"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
              <div className="py-6">
                <a
                  href={loginLink}
                  className={clsx(
                    '-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-neutral-900 hover:bg-neutral-50'
                  )}
                >
                  Log in <span aria-hidden="true">&rarr;</span>
                </a>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
};

const HeroBlurPoly1 = () => {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 -z-10 transform-gpu overflow-hidden blur-3xl -left-10 -top-40 sm:-top-60"
    >
      <motion.div
        animate={{
          clipPath: [
            'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            'polygon(72% 47%, 97% 63%, 94% 28%, 83% 2%, 77% 7%, 69% 34%, 57% 63%, 49% 69%, 44% 59%, 40% 37%, 24% 77%, 5% 67%, 22% 97%, 32% 73%, 81% 93%, 72% 47%)',
            'polygon(70% 50%, 94% 65%, 91% 30%, 81% 4%, 74% 12%, 66% 36%, 54% 64%, 46% 70%, 41% 60%, 35% 40%, 21% 78%, 10% 70%, 27% 94%, 37% 70%, 86% 89%, 70% 50%)',
            'polygon(72% 47%, 97% 63%, 94% 28%, 83% 2%, 77% 7%, 69% 34%, 57% 63%, 49% 69%, 44% 59%, 40% 37%, 24% 77%, 5% 67%, 22% 97%, 32% 73%, 81% 93%, 72% 47%)',
            'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
          ],
          left: [-100, -80, -60, -80, -100],
          rotate: [10, 20, 30, 20, 10]
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'linear',
          times: [0, 0.25, 0.5, 0.75, 1]
        }}
        className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-primary-500 to-primary-500 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
      />
    </div>
  );
};

const HeroBlurPoly2 = () => {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-[24vh] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[36vh]"
    >
      <motion.div
        animate={{
          clipPath: [
            'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            'polygon(72% 47%, 97% 63%, 94% 28%, 83% 2%, 77% 7%, 69% 34%, 57% 63%, 49% 69%, 44% 59%, 40% 37%, 24% 77%, 5% 67%, 22% 97%, 32% 73%, 81% 93%, 72% 47%)',
            'polygon(70% 50%, 94% 65%, 91% 30%, 81% 4%, 74% 12%, 66% 36%, 54% 64%, 46% 70%, 41% 60%, 35% 40%, 21% 78%, 10% 70%, 27% 94%, 37% 70%, 86% 89%, 70% 50%)',
            'polygon(72% 47%, 97% 63%, 94% 28%, 83% 2%, 77% 7%, 69% 34%, 57% 63%, 49% 69%, 44% 59%, 40% 37%, 24% 77%, 5% 67%, 22% 97%, 32% 73%, 81% 93%, 72% 47%)',
            'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
          ]
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'linear',
          times: [0, 0.25, 0.5, 0.75, 1]
        }}
        className="relative left-[calc(50%)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-primary-500 to-primary-500 opacity-20 sm:left-[calc(50%+12rem)] sm:w-[72.1875rem]"
      />
    </div>
  );
};

const HeroSection = () => {
  return (
    <div className="relative isolate px-8 pt-14 lg:px-12 overflow-hidden">
      <HeroBlurPoly1 />
      <motion.div
        initial={{ opacity: 0, translateY: 50 }}
        animate={{ opacity: 1, translateY: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        viewport={{ once: true }}
        className="mx-auto max-w-7xl pb-16 pt-10 sm:pb-24 lg:flex lg:pt-40"
      >
        <div className="max-w-2xl lg:mx-0 lg:shrink-0 lg:pt-8">
          <h1 className="mt-8 text-pretty text-5xl font-semibold tracking-tight text-neutral-900 sm:text-7xl">
            Professional AI for Insurance Servicing
          </h1>
          <p className="mt-8 text-pretty text-lg text-neutral-600 sm:text-xl/8">
            Cara grows your insurance agency, brokerage, or MGA with domain-specific AI that
            automates servicing and assists with sales.
          </p>
          <div className="mt-4 flex items-center gap-x-6">
            <PrimaryButton href={demoLink} target="_blank" className="xs:max-w-64">
              Book a Demo
            </PrimaryButton>
          </div>
        </div>
        <div className="mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
          <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
            <div className="-m-2 rounded-xl bg-neutral-900/5 p-2 ring-1 ring-inset ring-neutral-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
              <img
                alt="Cara's dashboard"
                src="/images/product-home.png"
                width={2432}
                height={1442}
                className="w-[76rem] rounded-md shadow-2xl ring-1 ring-neutral-900/10"
              />
            </div>
          </div>
        </div>
      </motion.div>
      <HeroBlurPoly2 />
    </div>
  );
};

const PageSection = (props: React.PropsWithChildren<{ id?: string; className?: string }>) => (
  <div id={props.id} className={props.className || 'py-16 sm:py-24'}>
    <motion.div
      initial={{ opacity: 0, translateY: 100 }}
      whileInView={{ opacity: 1, translateY: 0 }}
      transition={{ duration: 0.75, ease: 'easeOut' }}
      viewport={{ once: true }}
    >
      <div className="mx-auto max-w-7xl px-8 lg:px-12">{props.children}</div>
    </motion.div>
  </div>
);

const SectionTitle = (
  props: React.PropsWithChildren<{ title: string; subtitle: string; content?: string }>
) => (
  <div className="mx-auto max-w-2xl lg:max-w-3xl sm:text-center">
    <h2 className="text-base/7 font-semibold text-primary-500">{props.subtitle}</h2>
    <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-neutral-900 sm:text-balance sm:text-5xl">
      {props.title}
    </p>
    {props.content && <p className="mt-6 text-lg/8 text-neutral-600">{props.content}</p>}
  </div>
);

const PartnersSection = () => {
  const partners = [
    { src: '/images/PIA-National-Logo.png', alt: 'PIA National' },
    { src: '/images/IAOA.png', alt: 'IAOA' },
    { src: '/images/catalyit.png', alt: 'Catalyit' },
    // { src: '/images/Big-I-Alliance.jpg', alt: 'Big I Alliance' },
    // { src: '/images/maia.jpeg', alt: 'Massachusetts Association of Insurance Agents' },
    { src: '/images/abu.png', alt: 'ABU' }
    // { src: '/images/iiah.png', alt: 'IIAH' },
  ];

  return (
    <PageSection id="partnerships" className="py-0 pb-16">
      <div className="mx-auto mt-16 max-w-lg sm:max-w-xl lg:max-w-none">
        <div className="text-center">
          <h2 className="text-base/7 font-normal text-neutral-400">In partnership with</h2>
        </div>
      </div>
      <div className="mx-auto mt-8 max-w-7xl px-8 lg:px-12">
        <div className="grid gap-6 grid-cols-8">
          {partners.map((partner) => (
            <img
              key={partner.alt}
              alt={partner.alt}
              src={partner.src}
              className="col-span-6 flex self-center sm:col-span-4 md:col-span-2 relative h-10 w-full object-contain rounded"
            />
          ))}
        </div>
      </div>
    </PageSection>
  );
};

const ModulesSection = () => {
  const stats = [
    { id: 1, name: 'Servicing workflows automated*', value: '70%' },
    { id: 2, name: 'Types of tasks Cara can handle', value: '30+' },
    { id: 3, name: "Cara's availability for your clients", value: '24/7' }
  ];

  const features = {
    copilot: [
      {
        name: 'Compare quotes and contracts.',
        description:
          'Analyze quotes and vendor contracts to summarize key points and identify coverage gaps.',
        icon: PencilSquareIcon
      },
      {
        name: 'Analyze policy documents.',
        description:
          'Drag and drop policy documents to search and summarize coverage details for your customers.',
        icon: InboxArrowDownIcon
      },
      {
        name: 'Quote and bind automation.',
        description: 'Automate application intake, form generation, and bind submissions.',
        icon: BriefcaseIcon
      }
    ],
    servicing: [
      {
        name: 'Unified ticket management.',
        description: 'Manage all customer requests in a single place.',
        icon: InboxStackIcon
      },
      {
        name: 'Automate customer inquiries.',
        description:
          'AI summarizes emails and phone calls, generates action items, and draft responses for your approval.',
        icon: ChatBubbleBottomCenterTextIcon
      },
      {
        name: 'COI generation.',
        description: 'Let Cara generate certificates of insurance for you to review and send.',
        icon: DocumentCheckIcon
      }
    ],
    knowledge: [
      {
        name: 'Find answers fast.',
        description: 'Query and search policy documents and contracts with generative AI.',
        icon: MagnifyingGlassIcon
      },
      {
        name: 'Extract insights across data.',
        description: 'Combine multiple documents and sources to get holistic insights.',
        icon: ChartBarIcon
      },
      {
        name: 'Private, encrypted, and secure.',
        description: 'Your data is private to you and your customers.',
        icon: LockClosedIcon
      }
    ],
    communication: [
      {
        name: 'Voice AI.',
        description:
          'Cara can answer inbound calls, collect customer info, triage requests, handle claim FNOL, and service common needs.',
        icon: PhoneIcon
      },
      {
        name: 'Email AI.',
        description: 'Cara can handle email inquiries and assist agents with servicing tasks.',
        icon: EnvelopeIcon
      },
      {
        name: 'White-labeled policyholder portal.',
        description: 'Clients can access policy information and generate certificates digitally.',
        icon: ComputerDesktopIcon
      }
    ]
  };

  return (
    <PageSection id="product">
      <SectionTitle
        title="The service solution built for insurance"
        subtitle="Independent modules. One integrated system."
        content="Choose the modules that make sense for you, and see how Cara's integrated design delivers compounding value."
      />
      <div className="mt-10 grid grid-cols-1 gap-8 sm:mt-16 lg:grid-cols-2 lg:grid-rows-2">
        <div className="relative">
          <div className="absolute inset-px rounded-lg bg-white max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
          <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-t-[calc(2rem+1px)] lg:rounded-tl-[calc(2rem+1px)]">
            <div className="py-16 px-10">
              <h3 className="text-sm/4 font-semibold text-primary-500">
                A personal assistant for every agent
              </h3>
              <h3 className="mt-2 text-xl font-medium tracking-tight text-neutral-900">Copilot</h3>
              <p className="mt-2 max-w-lg text-base/6 text-neutral-600">
                Delegate complex tasks to AI assistants to automate workflows and drive sales.
              </p>
              <div className="mt-12 flex gap-x-6">
                <dl className="grid grid-cols-1 gap-8 text-sm/7 text-neutral-600">
                  {features.copilot.map((feature) => (
                    <div key={feature.name} className="relative pl-9 flex flex-col lg:block">
                      <dt className="inline font-semibold text-neutral-900">
                        <feature.icon
                          aria-hidden="true"
                          className="absolute left-0 top-1 size-5 text-primary-500"
                        />
                        {feature.name}
                      </dt>{' '}
                      <dd className="inline">{feature.description}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-neutral-200 max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
        </div>
        <div className="relative">
          <div className="absolute inset-px rounded-lg bg-white lg:rounded-tr-[2rem]" />
          <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-tr-[calc(2rem+1px)]">
            <div className="py-16 px-10">
              <h3 className="text-sm/4 font-semibold text-primary-500">
                Unified service and ticketing system
              </h3>
              <h3 className="mt-2 text-xl font-medium tracking-tight text-neutral-900">Service</h3>
              <p className="mt-2 max-w-lg text-base/6 text-neutral-600">
                Collaborate and automatically resolve customer requests in an AI-first workspace.
              </p>
              <div className="mt-12 flex gap-x-6">
                <dl className="grid grid-cols-1 gap-8 text-sm/7 text-neutral-600">
                  {features.servicing.map((feature) => (
                    <div key={feature.name} className="relative pl-9 flex flex-col lg:block">
                      <dt className="inline font-semibold text-neutral-900">
                        <feature.icon
                          aria-hidden="true"
                          className="absolute left-0 top-1 size-5 text-primary-500"
                        />
                        {feature.name}
                      </dt>{' '}
                      <dd className="inline">{feature.description}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-neutral-200 lg:rounded-tr-[2rem]" />
        </div>
        <div className="relative">
          <div className="absolute inset-px rounded-lg bg-white lg:rounded-bl-[2rem]" />
          <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-bl-[calc(2rem+1px)]">
            <div className="py-16 px-10">
              <h3 className="text-sm/4 font-semibold text-primary-500">
                Support your clients anywhere, 24/7
              </h3>
              <h3 className="mt-2 text-xl font-medium tracking-tight text-neutral-900">
                Communication
              </h3>
              <p className="mt-2 max-w-lg text-base/6 text-neutral-600">
                24/7 self-serve customer support across web, email, and phone calls.
              </p>
              <div className="mt-12 flex gap-x-6">
                <dl className="grid grid-cols-1 gap-8 text-sm/7 text-neutral-600">
                  {features.communication.map((feature) => (
                    <div key={feature.name} className="relative pl-9 flex flex-col lg:block">
                      <dt className="inline font-semibold text-neutral-900">
                        <feature.icon
                          aria-hidden="true"
                          className="absolute left-0 top-1 size-5 text-primary-500"
                        />
                        {feature.name}
                      </dt>{' '}
                      <dd className="inline">{feature.description}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-neutral-200 lg:rounded-bl-[2rem]" />
        </div>
        <div className="relative">
          <div className="absolute inset-px rounded-lg bg-white" />
          <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)]">
            <div className="py-16 px-10">
              <h3 className="text-sm/4 font-semibold text-primary-500">
                Leverage data for smarter servicing
              </h3>
              <h3 className="mt-2 text-xl font-medium tracking-tight text-neutral-900">
                Knowledge
              </h3>
              <p className="mt-2 max-w-lg text-base/6 text-neutral-600">
                Upload, manage, and analyze policies and documents using generative AI.
              </p>
              <div className="mt-12 flex gap-x-6">
                <dl className="grid grid-cols-1 gap-8 text-sm/7 text-neutral-600">
                  {features.knowledge.map((feature) => (
                    <div key={feature.name} className="relative pl-9 flex flex-col lg:block">
                      <dt className="inline font-semibold text-neutral-900">
                        <feature.icon
                          aria-hidden="true"
                          className="absolute left-0 top-1 size-5 text-primary-500"
                        />
                        {feature.name}
                      </dt>{' '}
                      <dd className="inline">{feature.description}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-neutral-200 lg:rounded-br-[2rem]" />
        </div>
      </div>
      <p className="mt-16 text-lg/8 text-neutral-600 sm:text-center">
        Cara returns 10+ hours per week to each agent and service rep, and drives higher customer
        satisfaction.
      </p>
      <dl className="mt-16 grid grid-cols-1 gap-1 overflow-hidden rounded-2xl text-center sm:grid-cols-3">
        {stats.map((stat) => (
          <div key={stat.id} className="flex flex-col bg-neutral-400/5 p-8">
            <dt className="text-sm/6 font-medium text-neutral-600">{stat.name}</dt>
            <dd className="order-first text-3xl font-semibold tracking-tight text-neutral-900">
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
    </PageSection>
  );
};

const UseCasesSection = () => {
  const [selectedUseCaseId, setSelectedUseCaseId] = useState('A');
  const useCases = {
    A: {
      title: 'Email assistant',
      description:
        'Forward emails or include Cara in email threads to triage service requests, draft customer responses, and automate tasks with your final approval.',
      image: '/images/email_assistant.png',
      alt: 'Email assistant'
    },
    B: {
      title: 'Research and compare',
      description:
        'Drag and drop quotes, contracts, and policy documents to compare coverage, extract key insights, and draft customer responses and quote presentations.',
      image: '/images/compare_coverage.png',
      alt: 'Research and compare'
    },
    C: {
      title: 'COI generation',
      description:
        'Create, manage, and share certificates of insurance in seconds. Cara can read policy documents and surface relevant endorsements and terms of coverage to help ensure certificates are correct.',
      image: '/images/coi_generation.png',
      alt: 'COI generation'
    },
    D: {
      title: 'Triage inbound calls',
      description:
        'Cara can handle inbound phone calls, triage leads and customers across agency or individual phone lines, and track outstanding service requests for your team.',
      image: '/images/triage_inbound_calls.png',
      alt: 'Triage inbound calls'
    },
    E: {
      title: 'Explore policy documents',
      description:
        'Upload policy documents and explore policy coverage, exclusions and terms to provide fast and efficient customer service.',
      image: '/images/explore_policy.png',
      alt: 'Explore policy documents'
    },
    F: {
      title: 'Document collection',
      description:
        'Collect and process applications, carrier supplementary forms, and bind orders via email and the white-labeled client portal.',
      image: '/images/document_collection.png',
      alt: 'Document collection'
    }
  };

  const selectedUseCase = useCases[selectedUseCaseId];

  return (
    <PageSection id="features">
      <SectionTitle
        title="Designed to augment everyone at your agency"
        subtitle="Tailor-fit to your use case"
        content=""
      />
      <div className="mt-10 grid grid-cols-1 gap-8 sm:mt-16">
        <div className="flex flex-wrap gap-2.5 items-center justify-center">
          {Object.entries(useCases).map(([key, useCase]) => (
            <button
              key={key}
              className={clsx(
                'text-sm px-4 py-1 rounded-full transition-all duration-75',
                selectedUseCaseId === key
                  ? 'bg-neutral-700 text-white border border-neutral-700'
                  : 'bg-neutral-50 text-neutral-900 border border-neutral-300 hover:bg-neutral-100'
              )}
              onClick={() => setSelectedUseCaseId(key)}
            >
              {useCase.title}
            </button>
          ))}
        </div>
        <div className="mt-10 grid grid-cols-1 gap-8 sm:mt-16 lg:grid-cols-2">
          <div>
            <h3 className="text-2xl font-medium tracking-tight text-neutral-900">
              {selectedUseCase.title}
            </h3>
            <p className="mt-2 text-base/6 text-neutral-600">{selectedUseCase.description}</p>
          </div>
          <div>
            <div className="rounded-lg h-full flex justify-center items-center">
              <img
                src={selectedUseCase.image}
                alt={selectedUseCase.alt}
                className="max-w-lg lg:max-w-none w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </PageSection>
  );
};
const IntegrationsSection = () => {
  const integrations = [
    // Productivity tool integrations
    { src: '/images/gmail.png', alt: 'Google Workspace and Gmail' },
    { src: '/images/gcal.png', alt: 'Google Workspace and Calendar' },
    { src: '/images/outlook.png', alt: 'Microsoft Outlook' },
    // { src: '/images/aircall.png', alt: 'Native phone call forwarding' },
    // Agency management software integrations
    // { src: '/images/vertafore.png', alt: 'Vertafore AMS360' },
    // { src: '/images/hawksoft.png', alt: 'Hawksoft' },
    // { src: '/images/ezlynx.png', alt: 'EZLynx' },
    // Billing and financing integrations
    // { src: '/images/ascend.jpg', alt: 'Ascend' },
    // { src: '/images/quickbooks.svg', alt: 'Quickbooks' },
    // Document generation and signature integrations/
    // { src: '/images/dropboxsign.png', alt: 'Dropbox Sign' },
    // CRMs
    { src: '/images/salesforce.png', alt: 'Salesforce' },
    { src: '/images/hubspot.png', alt: 'HubSpot' }
  ];

  return (
    <PageSection id="integrations">
      <SectionTitle
        title="Cara works with all your favorite tools"
        subtitle="Built for your agency"
        content="Connect Cara to the tools you already use, from email to AMS and CRM."
      />
      <div className="mx-auto mt-16 max-w-lg sm:max-w-xl lg:max-w-none">
        <div className="grid gap-8 grid-cols-2 sm:grid-cols-5 lg:grid-cols-5">
          {integrations.map((integration) => (
            <img
              key={integration.alt}
              alt={integration.alt}
              src={integration.src}
              className="col-span-1 flex self-center lg:col-span-1 relative h-12 w-full object-contain rounded"
            />
          ))}
        </div>
        <div className="relative mt-16">
          <div className="absolute inset-0 flex justify-center items-center" aria-hidden="true">
            <div className="w-[480px] border-t border-neutral-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-base text-neutral-400">
              And many more integrations
            </span>
          </div>
        </div>
      </div>
    </PageSection>
  );
};

const CallToActionSection = () => {
  return (
    <PageSection id="book-demo">
      <div className="relative isolate overflow-hidden bg-gradient-to-b from-white to-primary-200/50 px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16">
        <h2 className="text-balance text-4xl font-semibold tracking-tight text-neutral-900 sm:text-5xl">
          See Cara in action
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-pretty text-lg/8 text-neutral-600">
          Get started in minutes and see how Cara can reimagine servicing for your agency.
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <PrimaryButton href={demoLink} target="_blank" className="xs:max-w-md">
            Book a Demo
          </PrimaryButton>
        </div>
        <motion.svg
          viewBox="0 0 1024 1024"
          aria-hidden="true"
          className="absolute left-1/2 top-1/3 -z-10 size-[96rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
        >
          <circle
            r={512}
            cx={512}
            cy={512}
            fill="url(#827591b1-ce8c-4110-b064-7cb85a0b1217)"
            fillOpacity="0.7"
          />
          <defs>
            <radialGradient id="827591b1-ce8c-4110-b064-7cb85a0b1217">
              <stop stopColor="#0ea5e9" />
              <stop offset={1} stopColor="#0ea5e9" />
            </radialGradient>
          </defs>
        </motion.svg>
      </div>
    </PageSection>
  );
};

const Footer = () => {
  const socialLinks = [
    // {
    //   name: 'Facebook',
    //   href: '#',
    //   icon: (props) => (
    //     <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
    //       <path
    //         fillRule="evenodd"
    //         d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
    //         clipRule="evenodd"
    //       />
    //     </svg>
    //   )
    // },
    // {
    //   name: 'Instagram',
    //   href: '#',
    //   icon: (props) => (
    //     <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
    //       <path
    //         fillRule="evenodd"
    //         d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
    //         clipRule="evenodd"
    //       />
    //     </svg>
    //   )
    // },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/withoyster',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      )
    },
    {
      name: 'X',
      href: 'https://x.com/oyster_hq',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
        </svg>
      )
    }
  ];

  return (
    <footer className="bg-white">
      <div className="mx-auto max-w-7xl px-8 py-12 md:flex md:items-center md:justify-between lg:px-12">
        <div className="flex justify-center gap-x-6 md:order-2">
          {socialLinks.map((item) => (
            <a
              key={item.name}
              href={item.href}
              target="_blank"
              className="text-neutral-400 hover:text-primary-700"
            >
              <span className="sr-only">{item.name}</span>
              <item.icon aria-hidden="true" className="size-6" />
            </a>
          ))}
        </div>
        <p className="mt-8 text-center text-sm/6 text-neutral-400 md:order-1 md:mt-0">
          &copy; 2025 Oyster Technologies, Inc. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default function Example() {
  return (
    <>
      <Header />
      <HeroSection />
      <PartnersSection />
      <ModulesSection />
      <UseCasesSection />
      {/* <CSRPhoneSection /> */}
      {/* <FeatureSection /> */}
      <IntegrationsSection />
      <CallToActionSection />
      <Footer />
    </>
  );
}

export const LandingPage = () => {
  return <Example />;
};
